import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card, CardContent, Typography, Grid, Box, Button, TextField,
    FormControl, InputLabel, Select, MenuItem, Switch, FormControlLabel,
    Alert, CircularProgress, InputAdornment, IconButton,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions
} from '@mui/material';
import { useNotify } from 'react-admin';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import ClearIcon from '@mui/icons-material/Clear';
import InfoIcon from '@mui/icons-material/Info';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { motion } from 'framer-motion';
import axios from 'axios';
import config from '../../config/config.json';

const host = config.production.host;
const getJwt = () => localStorage.getItem("feathers-jwt");

const ROLES = [
    { value: 'admin', label: 'Admin', color: '#e85a2c' },
    { value: 'gruppi', label: 'Gruppi', color: '#2196f3' }
];

export const UsersCreatePage = () => {
    const navigate = useNavigate();
    const notify = useNotify();

    const [formData, setFormData] = React.useState({
        email: '',
        password: '',
        confirmPassword: '',
        nome: '',
        cognome: '',
        roles: 'gruppi',
        status: true
    });

    const [saving, setSaving] = React.useState(false);
    const [errors, setErrors] = React.useState({});
    const [touched, setTouched] = React.useState({});
    const [errorDialogOpen, setErrorDialogOpen] = React.useState(false);
    const [showPassword, setShowPassword] = React.useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);

    const resolveUserGroup = async (jwt) => {
        const response = await axios.get(`${host}/tbl-gruppo`, {
            headers: { 'Authorization': `Bearer ${jwt}` },
            params: {
                flAttivo: 1,
                $limit: 200
            }
        });
        const groups = response.data?.data || response.data || [];
        return groups.find((g) => String(g.nome || '').trim().toLowerCase() === 'user') || null;
    };

    const resolveFanPlan = async (jwt) => {
        const response = await axios.get(`${host}/tbl-piani`, {
            headers: { 'Authorization': `Bearer ${jwt}` },
            params: {
                flAttivo: 1,
                $limit: 200
            }
        });
        const plans = response.data?.data || response.data || [];
        return plans.find((p) => String(p.nome || '').trim().toLowerCase() === 'fan') || null;
    };

    const ensureUserGroupAssociation = async (jwt, userId, gruppoId) => {
        const existingResponse = await axios.get(`${host}/tbl-users-gruppo`, {
            headers: { 'Authorization': `Bearer ${jwt}` },
            params: {
                usersId: parseInt(userId, 10),
                tbl_gruppoId: parseInt(gruppoId, 10),
                $limit: 1
            }
        });
        const existing = existingResponse.data?.data || [];
        if (existing.length > 0) return;

        await axios.post(`${host}/tbl-users-gruppo`, {
            usersId: parseInt(userId, 10),
            tbl_gruppoId: parseInt(gruppoId, 10),
            flVisibile: 1,
            flAttivo: 1,
            flAnnullato: 0,
            ordinamento: 0,
            flPrincipale: 1
        }, {
            headers: { 'Authorization': `Bearer ${jwt}` }
        });
    };

    const assignDefaultFanSubscription = async (jwt, userId) => {
        const userGroup = await resolveUserGroup(jwt);
        const fanPlan = await resolveFanPlan(jwt);

        if (!userGroup) {
            throw new Error('Gruppo "user" non trovato');
        }
        if (!fanPlan) {
            throw new Error('Piano "FAN" non trovato o non attivo');
        }

        await ensureUserGroupAssociation(jwt, userId, userGroup.id);

        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const dataInizio = `${year}-${month}-${day}T${hours}:${minutes}`;

        const procResponse = await axios.get(`${host}/dynamic-proc`, {
            headers: { 'Authorization': `Bearer ${jwt}` },
            params: {
                proc_name: 'proc_crea_users_piano',
                in_userId: parseInt(userId, 10),
                in_pianoId: parseInt(fanPlan.id, 10),
                in_gruppoId: parseInt(userGroup.id, 10),
                in_durataType: 'mensile',
                in_dataInizio: dataInizio
            }
        });

        const out = procResponse?.data?.output_params || {};
        if (Number(out.out_esito) !== 1) {
            throw new Error(out.out_msg || 'Errore in proc_crea_users_piano per piano FAN');
        }
    };

    const validateField = (name, value) => {
        const newErrors = { ...errors };

        switch (name) {
            case 'email':
                if (!value.trim()) {
                    newErrors.email = 'L\'email è obbligatoria';
                } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                    newErrors.email = 'Email non valida';
                } else {
                    delete newErrors.email;
                }
                break;

            case 'password':
                if (!value) {
                    newErrors.password = 'La password è obbligatoria';
                } else if (value.length < 6) {
                    newErrors.password = 'La password deve essere di almeno 6 caratteri';
                } else {
                    delete newErrors.password;
                }
                if (formData.confirmPassword && value !== formData.confirmPassword) {
                    newErrors.confirmPassword = 'Le password non coincidono';
                } else if (formData.confirmPassword) {
                    delete newErrors.confirmPassword;
                }
                break;

            case 'confirmPassword':
                if (!value) {
                    newErrors.confirmPassword = 'Conferma la password';
                } else if (value !== formData.password) {
                    newErrors.confirmPassword = 'Le password non coincidono';
                } else {
                    delete newErrors.confirmPassword;
                }
                break;

            case 'nome':
                if (!value.trim()) {
                    newErrors.nome = 'Il nome è obbligatorio';
                } else if (value.trim().length < 2) {
                    newErrors.nome = 'Il nome deve essere di almeno 2 caratteri';
                } else {
                    delete newErrors.nome;
                }
                break;

            case 'cognome':
                if (!value.trim()) {
                    newErrors.cognome = 'Il cognome è obbligatorio';
                } else if (value.trim().length < 2) {
                    newErrors.cognome = 'Il cognome deve essere di almeno 2 caratteri';
                } else {
                    delete newErrors.cognome;
                }
                break;

            default:
                delete newErrors[name];
                break;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleFieldChange = (name, value) => {
        setFormData(prev => ({ ...prev, [name]: value }));
        setTouched(prev => ({ ...prev, [name]: true }));
        validateField(name, value);
    };

    const validateForm = () => {
        const fieldsToValidate = ['email', 'password', 'confirmPassword', 'nome', 'cognome'];
        let isValid = true;

        fieldsToValidate.forEach(field => {
            if (!validateField(field, formData[field])) {
                isValid = false;
            }
            setTouched(prev => ({ ...prev, [field]: true }));
        });

        return isValid;
    };

    const handleSave = async () => {
        if (!validateForm()) {
            notify("Correggere gli errori nel form", { type: 'error' });
            return;
        }

        try {
            setSaving(true);
            const jwt = getJwt();

            const dataToSend = {
                email: formData.email.trim().toLowerCase(),
                password: formData.password,
                nome: formData.nome.trim(),
                cognome: formData.cognome.trim(),
                roles: formData.roles,
                status: formData.status ? 1 : 0,
                registrationSource: 'backoffice'
            };

            // Crea l'utente. L'associazione al gruppo avverrà nel backend tramite hook.
            const userResponse = await axios.post(`${host}/users`, dataToSend, {
                headers: { 'Authorization': `Bearer ${jwt}` }
            });

            const userId = userResponse.data.id;
            try {
                await assignDefaultFanSubscription(jwt, userId);
            } catch (assignError) {
                console.error("Errore assegnazione automatica FAN:", assignError);
                notify(`Utente creato, ma assegnazione FAN automatica fallita: ${assignError.message}`, { type: 'warning' });
            }
            notify(`Utente "${formData.nome} ${formData.cognome}" creato con successo`, { type: 'success' });
            navigate(`/users/${userId}`);

        } catch (error) {
            console.error("Errore creazione utente:", error);
            if (error.response?.data?.errors?.some(e => e.message === 'email must be unique')) {
                setErrorDialogOpen(true);
            } else if (error.response?.status === 409 || error.response?.data?.message?.includes('email')) {
                notify("Email già esistente", { type: 'error' });
            } else {
                notify(error.response?.data?.message || "Errore nella creazione dell'utente", { type: 'error' });
            }
        } finally {
            setSaving(false);
        }
    };

    const handleReset = () => {
        setFormData({
            email: '', password: '', confirmPassword: '', nome: '', cognome: '', roles: 'gruppi', status: true
        });
        setErrors({});
        setTouched({});
    };

    const hasErrors = Object.keys(errors).length > 0;
    const canSave = formData.email.trim() && formData.password && formData.confirmPassword &&
        formData.nome.trim() && formData.cognome.trim() && !hasErrors;

    const selectedRole = ROLES.find(r => r.value === formData.roles);

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f6f7fb', minHeight: '100vh' }}>
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>

                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                            <PersonIcon sx={{ fontSize: 32, verticalAlign: 'middle', mr: 1 }} />
                            Crea Nuovo Utente
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Inserisci i dettagli per creare un nuovo account. I permessi e i gruppi potranno essere gestiti dopo la creazione.
                        </Typography>
                    </Box>
                    <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/users')}>
                        Indietro
                    </Button>
                </Box>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={8}>
                        <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                                    <InfoIcon color="primary" />
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>Informazioni Utente</Typography>
                                </Box>

                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Nome *"
                                            value={formData.nome}
                                            onChange={(e) => handleFieldChange('nome', e.target.value)}
                                            error={touched.nome && !!errors.nome}
                                            helperText={touched.nome && errors.nome}
                                            placeholder="Inserisci il nome"
                                            autoComplete="off"
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Cognome *"
                                            value={formData.cognome}
                                            onChange={(e) => handleFieldChange('cognome', e.target.value)}
                                            error={touched.cognome && !!errors.cognome}
                                            helperText={touched.cognome && errors.cognome}
                                            placeholder="Inserisci il cognome"
                                            autoComplete="off"
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Email *"
                                            type="email"
                                            value={formData.email}
                                            onChange={(e) => handleFieldChange('email', e.target.value)}
                                            error={touched.email && !!errors.email}
                                            helperText={touched.email && errors.email}
                                            placeholder="utente@esempio.com"
                                            autoComplete="off"
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <EmailIcon />
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Password *"
                                            type={showPassword ? 'text' : 'password'}
                                            value={formData.password}
                                            onChange={(e) => handleFieldChange('password', e.target.value)}
                                            error={touched.password && !!errors.password}
                                            helperText={touched.password && errors.password || "Minimo 6 caratteri"}
                                            autoComplete="new-password"
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <LockIcon />
                                                    </InputAdornment>
                                                ),
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Conferma Password *"
                                            type={showConfirmPassword ? 'text' : 'password'}
                                            value={formData.confirmPassword}
                                            onChange={(e) => handleFieldChange('confirmPassword', e.target.value)}
                                            error={touched.confirmPassword && !!errors.confirmPassword}
                                            helperText={touched.confirmPassword && errors.confirmPassword}
                                            autoComplete="new-password"
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <LockIcon />
                                                    </InputAdornment>
                                                ),
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                                                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <FormControl fullWidth>
                                            <InputLabel>Ruolo Iniziale</InputLabel>
                                            <Select
                                                value={formData.roles}
                                                label="Ruolo Iniziale"
                                                onChange={(e) => handleFieldChange('roles', e.target.value)}
                                            >
                                                {ROLES.map(role => (
                                                    <MenuItem key={role.value} value={role.value}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: role.color }} />
                                                            {role.label}
                                                        </Box>
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Alert severity="info" variant="outlined">
                                            Una volta creato, l'utente verrà associato automaticamente al gruppo predefinito. Potrai modificare l'associazione e i colori nella pagina di modifica.
                                        </Alert>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Card sx={{ borderRadius: 3, boxShadow: 2, mb: 3 }}>
                            <CardContent sx={{ p: 3, textAlign: 'center' }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Anteprima Utente</Typography>
                                <PersonIcon sx={{ fontSize: 60, color: selectedRole?.color || '#2196f3', mb: 2 }} />
                                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                                    {formData.nome || formData.cognome ? `${formData.nome} ${formData.cognome}` : 'Nome Cognome'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    {formData.email || 'email@esempio.com'}
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: selectedRole?.color }} />
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {selectedRole?.label || 'Gruppi'}
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>

                        <Card sx={{ borderRadius: 3, boxShadow: 2, mb: 3 }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Impostazioni</Typography>
                                <FormControlLabel
                                    control={<Switch checked={formData.status} onChange={(e) => handleFieldChange('status', e.target.checked)} color="primary" />}
                                    label="Utente attivo"
                                />
                            </CardContent>
                        </Card>

                        {hasErrors && (
                            <Alert severity="error" sx={{ mb: 3 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Correggere i seguenti errori:</Typography>
                                <ul style={{ margin: 0, paddingLeft: 16 }}>
                                    {Object.values(errors).map((error, index) => (
                                        <li key={index}><Typography variant="body2">{error}</Typography></li>
                                    ))}
                                </ul>
                            </Alert>
                        )}

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Button
                                variant="contained"
                                size="large"
                                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                onClick={handleSave}
                                disabled={!canSave || saving}
                                sx={{ py: 1.5 }}
                            >
                                {saving ? 'Salvataggio...' : 'Crea Utente'}
                            </Button>
                            <Button variant="outlined" startIcon={<ClearIcon />} onClick={handleReset} disabled={saving}>
                                Reset Form
                            </Button>
                        </Box>
                    </Grid>
                </Grid>

                <Box sx={{ mt: 4, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">* Campi obbligatori</Typography>
                </Box>

            </motion.div>

            <Dialog
                open={errorDialogOpen}
                onClose={() => setErrorDialogOpen(false)}
            >
                <DialogTitle>Errore Creazione Utente</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        L'email è già adoperata su altro account
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setErrorDialogOpen(false)} color="primary" autoFocus>
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
};
